import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { taskApi, type Task } from '../api';
import Select from '../components/common/Select';
import CompleteTaskModal from '../components/common/CompleteTaskModal';
import EditTaskModal from '../components/common/EditTaskModal';
import { humaniseStatus } from '../lib/labels';

type MatterGroup = {
  matter_id: string;
  matter_name: string;
  matter_reference: string | null;
  tasks: Task[];
};

export default function Tasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [status, setStatus] = useState<'open' | 'completed' | ''>('open');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Default = current stage only, so a long-running matter doesn't bury the
  // advocate under stage-0 tasks they already moved past. Toggle to see all.
  const [stageScope, setStageScope] = useState<'current' | 'all'>('current');
  // Default = hide tasks owned by the attorney (visible read-only only when
  // the advocate explicitly opts in). Advocate-only or unscoped tasks always show.
  const [showAttorneyTasks, setShowAttorneyTasks] = useState(false);
  // Active task in the time-entry prompt; null when no modal is open.
  const [completingTask, setCompletingTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  // Matter IDs whose task list is currently collapsed. Default = expanded.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleGroup = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const load = async () => {
    setLoading(true);
    try {
      const r = await taskApi.list({ status: status || undefined });
      setTasks(r.tasks || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const asBool = (v: Task['billable']) => v === true || v === 'true';

  // Billable tasks open the time-entry prompt; non-billable complete inline.
  const onCompleteClick = async (t: Task) => {
    if (asBool(t.billable)) {
      setCompletingTask(t);
      return;
    }
    setBusy(String(t.ROWID));
    try { await taskApi.complete(t.ROWID); await load(); }
    finally { setBusy(null); }
  };

  const onDeleteClick = async (t: Task) => {
    if (!confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    setBusy(String(t.ROWID));
    try { await taskApi.remove(t.ROWID); await load(); }
    finally { setBusy(null); }
  };

  // Apply current-stage + owner-role filters before grouping. Both filters
  // are inclusive of "no info available" — a task without owner_role is treated
  // as either-side; a task without matter_current_stage_id passes through.
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (stageScope === 'current') {
        const cur = (t as Task & { matter_current_stage_id?: string | null }).matter_current_stage_id;
        const stg = t.stage_id != null ? String(t.stage_id) : null;
        // If we don't know the matter's current stage, show the task. If we do,
        // show only tasks for that stage (or stage-less tasks).
        if (cur && stg && stg !== cur) return false;
      }
      const owner = (t as Task & { owner_role?: string | null }).owner_role;
      if (!showAttorneyTasks && owner === 'attorney') return false;
      return true;
    });
  }, [tasks, stageScope, showAttorneyTasks]);

  // Group filtered tasks by matter so the dashboard reads as a per-matter
  // punch list rather than a flat queue. Sorts by matter name for stable ordering.
  const groups = useMemo<MatterGroup[]>(() => {
    const byMatter = new Map<string, MatterGroup>();
    for (const t of filteredTasks) {
      const id = String(t.matter_id);
      if (!byMatter.has(id)) {
        byMatter.set(id, {
          matter_id: id,
          matter_name: t.matter_name || `Matter #${id}`,
          matter_reference: t.matter_reference || null,
          tasks: [],
        });
      }
      byMatter.get(id)!.tasks.push(t);
    }
    return [...byMatter.values()].sort((a, b) =>
      a.matter_name.localeCompare(b.matter_name));
  }, [filteredTasks]);

  return (
    <div className="stack">
      <div className="row">
        <h1 className="grow">Tasks</h1>
        {groups.length > 0 && (
          <>
            <button className="ghost" onClick={() => setCollapsed(new Set())}>Expand all</button>
            <button className="ghost" onClick={() => setCollapsed(new Set(groups.map((g) => g.matter_id)))}>Collapse all</button>
          </>
        )}
        <Select
          value={stageScope}
          onChange={(v) => setStageScope(v as typeof stageScope)}
          style={{ width: 200 }}
          options={[
            { value: 'current', label: 'Current stage only' },
            { value: 'all', label: 'All stages (incl. prior)' },
          ]}
        />
        <label className="inline">
          <input
            type="checkbox"
            checked={showAttorneyTasks}
            onChange={(e) => setShowAttorneyTasks(e.target.checked)}
          />
          Show attorney tasks
        </label>
        <Select
          value={status}
          onChange={(v) => setStatus(v as typeof status)}
          style={{ width: 180 }}
          options={[
            { value: '', label: 'All tasks' },
            { value: 'open', label: 'Open' },
            { value: 'completed', label: 'Completed' },
          ]}
        />
      </div>

      {loading ? (
        <div className="card"><div className="muted">Loading…</div></div>
      ) : groups.length === 0 ? (
        <div className="card">
          <p className="muted">No tasks match. Tasks auto-create when you advance a matter through procedural stages.</p>
        </div>
      ) : (
        <div className="stack">
          {groups.map((g) => {
            const isCollapsed = collapsed.has(g.matter_id);
            return (
            <div className="card" key={g.matter_id}>
              <div
                className="row"
                style={{ alignItems: 'baseline', marginBottom: isCollapsed ? 0 : 12, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleGroup(g.matter_id)}
                role="button"
                aria-expanded={!isCollapsed}
              >
                <span style={{ display: 'inline-block', width: 16, fontSize: 12, color: 'var(--color-text-muted)' }}>
                  {isCollapsed ? '▸' : '▾'}
                </span>
                <h3 style={{ margin: 0 }} className="grow">
                  <Link to={`/matters/${g.matter_id}`} onClick={(e) => e.stopPropagation()}>
                    {g.matter_name}
                  </Link>
                </h3>
                {g.matter_reference && (
                  <span className="muted" style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>
                    {g.matter_reference}
                  </span>
                )}
                <span className="pill">{g.tasks.length} task{g.tasks.length === 1 ? '' : 's'}</span>
              </div>
              {!isCollapsed && (
                // table-layout: fixed + explicit colgroup widths keep columns
                // aligned across groups. Without this, each <table> auto-sizes
                // its own columns from its longest task name and Priority /
                // Due / Status drift to different X coordinates per matter.
                <table style={{ tableLayout: 'fixed', width: '100%' }}>
                  <colgroup>
                    <col />                         {/* Task — flexes to fill */}
                    <col style={{ width: 110 }} />  {/* Priority */}
                    <col style={{ width: 120 }} />  {/* Due */}
                    <col style={{ width: 130 }} />  {/* Status */}
                    <col style={{ width: 240 }} />  {/* Actions */}
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Priority</th>
                      <th>Due</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.tasks.map((t) => (
                      <tr key={String(t.ROWID)}>
                        <td style={{ overflow: 'hidden' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                          {t.description && <div className="muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.description}</div>}
                        </td>
                        <td><span className={`pill ${t.priority === 'urgent' || t.priority === 'high' ? 'danger' : ''}`}>{t.priority}</span></td>
                        <td>{t.due_date || '—'}</td>
                        <td><span className="pill">{humaniseStatus(t.status)}</span></td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button
                            className="ghost"
                            style={{ padding: '4px 10px', fontSize: 12, marginRight: 4 }}
                            onClick={() => setEditingTask(t)}
                            disabled={busy === String(t.ROWID)}
                          >
                            Edit
                          </button>
                          <button
                            className="ghost"
                            style={{ padding: '4px 10px', fontSize: 12, marginRight: 4, color: 'var(--color-danger)' }}
                            onClick={() => onDeleteClick(t)}
                            disabled={busy === String(t.ROWID)}
                          >
                            Delete
                          </button>
                          {t.status !== 'completed' && t.status !== 'cancelled' && (
                            <button
                              className="primary"
                              onClick={() => onCompleteClick(t)}
                              disabled={busy === String(t.ROWID)}
                            >
                              {busy === String(t.ROWID) ? '…' : 'Complete'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );})}
        </div>
      )}

      {completingTask && (
        <CompleteTaskModal
          task={completingTask}
          onClose={() => setCompletingTask(null)}
          onCompleted={() => { setCompletingTask(null); load(); }}
        />
      )}

      {editingTask && (
        <EditTaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSaved={() => { setEditingTask(null); load(); }}
        />
      )}
    </div>
  );
}
